import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { timeEntries, schedules, locations, calculateEmployeePayroll } from '../data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import {
  Clock,
  Calendar,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from 'lucide-react';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  isSameDay,
  isWithinInterval,
} from 'date-fns';

const MyHoursEmployee = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Get user's time entries
  const myTimeEntries = useMemo(() => {
    return timeEntries.filter((entry) => entry.employeeId === user?.id);
  }, [user]);

  // Week entries
  const weekEntries = useMemo(() => {
    return myTimeEntries.filter((entry) => {
      const entryDate = parseISO(entry.date);
      return isWithinInterval(entryDate, { start: weekStart, end: weekEnd });
    });
  }, [myTimeEntries, weekStart, weekEnd]);

  // Calculate weekly stats
  const weeklyStats = useMemo(() => {
    const totalHours = weekEntries.reduce((sum, e) => sum + e.hoursWorked, 0);
    const totalPay = totalHours * (user?.hourlyRate || 0);
    const daysWorked = new Set(weekEntries.map((e) => e.date)).size;
    const avgHoursPerDay = daysWorked > 0 ? totalHours / daysWorked : 0;

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      totalPay: Math.round(totalPay * 100) / 100,
      daysWorked,
      avgHoursPerDay: Math.round(avgHoursPerDay * 100) / 100,
    };
  }, [weekEntries, user]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const location = locations.find((l) => l.id === user?.locationId);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Hours</h1>
        <p className="text-gray-500 mt-1">
          Track your time at {location?.name || 'your location'}
        </p>
      </div>

      {/* Week Navigation */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={handlePrevWeek}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={handleToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextWeek}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Employee ID:</span>
              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                {user?.employeeNumber}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Hours This Week</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {weeklyStats.totalHours}h
                </p>
              </div>
              <div className="bg-blue-500 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Earnings (Est.)</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  ${weeklyStats.totalPay.toLocaleString()}
                </p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Days Worked</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {weeklyStats.daysWorked}
                </p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg Hours/Day</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {weeklyStats.avgHoursPerDay}h
                </p>
              </div>
              <div className="bg-amber-500 p-3 rounded-xl">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries Table */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Time Entries for {format(weekStart, 'MMM d')} - {format(weekEnd, 'd')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">Date</TableHead>
                <TableHead className="font-medium">Clock In</TableHead>
                <TableHead className="font-medium">Clock Out</TableHead>
                <TableHead className="font-medium">Break</TableHead>
                <TableHead className="font-medium text-right">Hours</TableHead>
                <TableHead className="font-medium text-right">Earnings</TableHead>
                <TableHead className="font-medium">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {weekDays.map((day) => {
                const dayEntry = weekEntries.find(
                  (e) => e.date === format(day, 'yyyy-MM-dd')
                );
                const isToday = isSameDay(day, new Date());

                return (
                  <TableRow
                    key={day.toISOString()}
                    className={`${
                      isToday ? 'bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {format(day, 'EEEE')}
                        </p>
                        <p className="text-sm text-gray-500">
                          {format(day, 'MMM d')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {dayEntry ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {format(parseISO(dayEntry.clockIn), 'h:mm a')}
                          </span>
                          {dayEntry.photoIn && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              Photo
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {dayEntry?.clockOut ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {format(parseISO(dayEntry.clockOut), 'h:mm a')}
                          </span>
                          {dayEntry.photoOut && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              Photo
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {dayEntry ? (
                        <span className="text-gray-600">
                          {dayEntry.breakMinutes} min
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {dayEntry ? (
                        <span className="font-medium text-gray-900">
                          {dayEntry.hoursWorked.toFixed(2)}h
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {dayEntry ? (
                        <span className="font-medium text-purple-600">
                          ${(dayEntry.hoursWorked * (user?.hourlyRate || 0)).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {dayEntry ? (
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            dayEntry.status === 'approved'
                              ? 'bg-purple-100 text-purple-700'
                              : dayEntry.status === 'active'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {dayEntry.status}
                        </span>
                      ) : (
                        <span className="text-gray-400">No entry</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Totals */}
          <div className="bg-gray-50 p-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Week Total</span>
              <div className="flex items-center gap-8">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Hours</p>
                  <p className="font-semibold text-gray-900">
                    {weeklyStats.totalHours}h
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Estimated Earnings</p>
                  <p className="font-bold text-purple-600">
                    ${weeklyStats.totalPay.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyHoursEmployee;
